import { z } from 'zod';

export const MessageTypes = [
    'REPLY',
    'INTRO',
    'FOLLOW_UP',
    'QUESTION',
    'OFFER',
    'CALENDAR_INVITE',
    'AUTO_REPLY',
    'REMINDER',
    'OTHER',
] as const;

export const Intents = [
    'SALES',
    'FUNDRAISING',
    'CUSTOMER_SUCCESS',
    'NETWORKING',
    'HIRING',
    'PARTNERSHIP',
    'EVENT_INVITE',
    'SUPPORT',
    'MARKETING',
    'REFERRAL',
    'EDUCATION',
    'PRODUCT_FEEDBACK',
    'CONTRACT_NEGOTIATION',
    'RENEWAL',
    'UPSELL',
    'CROSS_SELL',
    'REACTIVATION',
    'INFORMATION_GATHERING',
    'RELATIONSHIP_MAINTENANCE',
    'PERSONAL',
    'OTHER',
] as const;

export const AttachmentTypes = [
    'DECK',
    'PROPOSAL',
    'CONTRACT',
    'WHITEPAPER',
    'CASE_STUDY',
    'TECHNICAL_SPEC',
    'PRICING_SHEET',
    'BROCHURE',
    'DATA_SHEET',
    'INVOICE',
    'RESUME',
    'PORTFOLIO',
    'REPORT',
    'FINANCIAL_STATEMENT',
    'SCREENSHOT',
    'VIDEO',
    'DEMO_RECORDING',
    'PRODUCT_SAMPLE',
    'TESTIMONIAL',
    'AGREEMENT',
    'TERM_SHEET',
    'CAP_TABLE',
    'PITCH_DECK',
    'IMPLEMENTATION_PLAN',
    'INTEGRATION_GUIDE',
    'ROI_CALCULATOR',
    'SECURITY_ASSESSMENT',
    'COMPLIANCE_DOCUMENT',
    'SOW',
    'MSA',
] as const;

export const UrgencyLevels = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'NONE'] as const;

export const ImportanceLevels = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'NONE'] as const;

export const DecisionTimeframes = [
    'IMMEDIATE',
    'SHORT_TERM',
    'MEDIUM_TERM',
    'LONG_TERM',
    'UNDETERMINED',
    'DEPENDENT_ON_EVENT',
] as const;

export const ConfidenceLevels = ['VERY_LOW', 'LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH'] as const;

export const Channels = [
    'LINKEDIN',
    'EMAIL',
    'TWITTER',
    'WHATSAPP',
    'SLACK',
    'TEAMS',
    'SMS',
    'PHONE',
    'IN_PERSON',
    'ZOOM',
    'GOOGLE_MEET',
    'TELEGRAM',
    'DISCORD',
    'OTHER',
] as const;

export const MessageMetadataComputedSchema = z.object({
    sentVia: z.enum(Channels).describe('The channel through which the message was sent.'),
    sentOn: z.string().describe('The ISO date and time the message was sent.'),
});

export const MessageMetadataGeneratedSchema = z
    .object({
        messageType: z
            .array(z.enum(MessageTypes))
            .describe('Structural role of the message, e.g. INTRO, QUESTION, or REPLY.'),

        language: z
            .string()
            .describe(
                'The message language (e.g. English, Swedish, German, Spanish, Vietnamese, Mandarin, Japanese, Emoji, Unknown etc.).',
            ),

        topics: z.array(z.string()).describe('Main topics discussed across the conversation.'),

        primaryIntent: z.enum(Intents).describe('The main purpose or goal of the message.'),

        attachmentTypes: z
            .array(z.enum(AttachmentTypes))
            .describe('Any file or media types attached to the message. May be empty.'),

        sentiment: z.string().describe('Overall sentiment in one or two words.'),

        tone: z.string().describe('Nuanced emotional tone of the message in one or two words.'),

        urgency: z.enum(UrgencyLevels).describe('How urgent the message is.'),

        importance: z.enum(ImportanceLevels).describe('How important the message is.'),

        objections: z.string().describe('Any objections or blockers mentioned. May be empty.'),

        decisionTimeframe: z
            .object({
                timeframe: z.enum(DecisionTimeframes).describe('When a decision or follow-up is expected.'),
                confidence: z
                    .enum(ConfidenceLevels)
                    .describe('Model confidence in its own timeframe estimation, from VERY_LOW to VERY_HIGH.'),
            })
            .describe('Estimated timeframe and confidence for next steps.'),

        messageSummary: z
            .string()
            .describe('This should be a single sentence that captures the main points of the message.'),
    })
    .describe('Full metadata schema for analyzing a single message.');

export const MessageMetadataSchema = MessageMetadataGeneratedSchema.merge(MessageMetadataComputedSchema);
